import React, { useEffect, useState } from "react";
import { Link as RouterLink, useHistory, useParams } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import AuthLayout from "../auth/AuthLayout";
import { PasswordField, StrengthMeter } from "../auth/AuthControls";
import {
  acceptInvitation,
  acceptInvitationWithPassword,
  authenticate,
  getProfile,
  previewInvitation,
  recordSignOut,
  registerFromInvitation,
} from "../../actions";

// Stored roles read the new way: owner reads as Admin; editor and viewer as Member.
const ROLE_LABELS = { admin: "Admin", member: "Member", owner: "Admin", editor: "Member", viewer: "Member" };
const MIN_PASSWORD_LENGTH = 10;

// Someone following an invitation link. Signed in with the invited address:
// join. Signed in as someone else: offer to switch accounts. Not signed in:
// create an account, or - with an account already - confirm the password
// here (which also works when a personal trial has ended and signing in
// on its own would be refused).
export default function InvitePage() {
  const { token } = useParams();
  const history = useHistory();

  // There is no "user" record cached in localStorage — only the JWT under
  // "auth". To learn who (if anyone) is signed in we ask the server, and
  // treat a failed profile call the same as being signed out.
  const hasToken = Boolean(localStorage.getItem("auth"));
  const [authChecked, setAuthChecked] = useState(!hasToken);
  const [authEmail, setAuthEmail] = useState(null);

  useEffect(() => {
    if (!hasToken) return undefined;
    let cancelled = false;
    getProfile(localStorage.getItem("auth")).then((res) => {
      if (cancelled) return;
      setAuthEmail(res?.success ? res.content?.email || null : null);
      setAuthChecked(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signedIn = hasToken && authChecked && Boolean(authEmail);

  const [invite, setInvite] = useState(null);
  const [problem, setProblem] = useState("");
  const [form, setForm] = useState({ firstName: "", lastName: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    previewInvitation(token).then((res) => {
      if (res?.success) setInvite(res.content);
      else setProblem(res?.msg || "This invitation link isn't valid.");
    });
  }, [token]);

  const join = async () => {
    setBusy(true);
    setError("");
    const res = await acceptInvitation(token);
    setBusy(false);
    if (res?.success) {
      history.push("/home");
      history.go(0);
    } else {
      setError(res?.msg || "Couldn't join. Ask whoever invited you to send a new invitation.");
    }
  };

  const register = async (e) => {
    e.preventDefault();
    if (form.password.length < MIN_PASSWORD_LENGTH) {
      setError(`Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    setBusy(true);
    setError("");
    const created = await registerFromInvitation(token, form);
    if (!created?.success) {
      setBusy(false);
      setError(created?.msg || "Couldn't create the account. Try again, or ask whoever invited you to send a new link.");
      return;
    }
    // Registering doesn't sign the browser in — do that ourselves, then land
    // on home like any other first sign-in.
    await signInAfterJoining(invite.email, form.password);
  };

  // The person is a member now. Sign in and land on home; if that fails, the
  // membership still stands, so send them to a plain sign-in that says so
  // rather than leaving them on a form that would fail again.
  const signInAfterJoining = async (email, secret) => {
    const session = await authenticate(email, secret);
    setBusy(false);
    if (session?.success) {
      history.push("/home");
      history.go(0);
    } else {
      history.push("/login?notice=joined");
    }
  };

  const joinWithPassword = async (e) => {
    e.preventDefault();
    if (!password) {
      setError("Enter your password.");
      return;
    }
    setBusy(true);
    setError("");
    const joined = await acceptInvitationWithPassword(token, password);
    if (!joined?.success) {
      setBusy(false);
      setError(joined?.msg || "Couldn't join. Try again, or ask whoever invited you to send a new invitation.");
      return;
    }
    await signInAfterJoining(joined.content?.email || invite.email, password);
  };

  const signInHere = () => history.push(`/login?next=${encodeURIComponent(`/invite/${token}`)}`);

  const loading = !problem && (!invite || (hasToken && !authChecked));

  let body;
  if (problem) {
    body = (
      <Stack spacing={2}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          This invitation can't be used
        </Typography>
        <Alert severity="warning">{problem}</Alert>
        <Typography variant="body2" color="text.secondary">
          Ask whoever invited you to send a new invitation, or sign in if you already have an account.
        </Typography>
        <Button variant="outlined" size="large" onClick={() => history.push(signedIn ? "/home" : "/login")}>
          {signedIn ? "Go to Business Flows" : "Sign in"}
        </Button>
      </Stack>
    );
  } else if (loading) {
    body = (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  } else {
    const workspaceName = (invite.workspace || invite.organisation || {}).name || "the workspace";
    const intro = (
      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
          Join {workspaceName}
        </Typography>
        <Typography color="text.secondary">
          {invite.invitedBy || "A colleague"} invited <strong>{invite.email}</strong> as{" "}
          <strong>{ROLE_LABELS[invite.role] || "Member"}</strong>.
        </Typography>
      </Box>
    );

    if (signedIn && authEmail.toLowerCase() !== invite.email.toLowerCase()) {
      body = (
        <Stack spacing={2}>
          {intro}
          <Alert severity="info">
            You're signed in as {authEmail}. This invitation is for {invite.email} — sign out and sign in with
            that address to accept it.
          </Alert>
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              recordSignOut();
              localStorage.clear();
              signInHere();
            }}
          >
            Sign out and switch account
          </Button>
        </Stack>
      );
    } else if (signedIn) {
      body = (
        <Stack spacing={2}>
          {intro}
          {error && <Alert severity="error">{error}</Alert>}
          <LoadingButton variant="contained" size="large" loading={busy} onClick={join} fullWidth>
            Join {workspaceName}
          </LoadingButton>
        </Stack>
      );
    } else if (invite.hasAccount) {
      body = (
        <Box component="form" onSubmit={joinWithPassword} noValidate>
          <Stack spacing={2.5}>
            {intro}
            <Typography variant="body2" color="text.secondary">
              You already have a Business Flows account. Enter your password to join and sign in.
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              label="Email"
              value={invite.email}
              disabled
              fullWidth
              helperText="Invitations are tied to the address they were sent to."
            />
            <Box>
              <PasswordField
                label="Password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                autoFocus
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Link component={RouterLink} to="/forget-password" variant="body2">
                  Forgot your password?
                </Link>
              </Box>
            </Box>
            <LoadingButton type="submit" variant="contained" size="large" loading={busy} disabled={!password} fullWidth>
              Join {workspaceName}
            </LoadingButton>
          </Stack>
        </Box>
      );
    } else {
      body = (
        <Box component="form" onSubmit={register} noValidate>
          <Stack spacing={2.5}>
            {intro}
            {error && <Alert severity="error">{error}</Alert>}
            <Grid container spacing={1.75}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First name"
                  autoComplete="given-name"
                  value={form.firstName}
                  onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last name"
                  autoComplete="family-name"
                  value={form.lastName}
                  onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Email"
                  value={invite.email}
                  disabled
                  fullWidth
                  helperText="Invitations are tied to the address they were sent to."
                />
              </Grid>
              <Grid item xs={12}>
                <PasswordField
                  label="Choose a password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  helperText={`At least ${MIN_PASSWORD_LENGTH} characters.`}
                  required
                  fullWidth
                />
                <StrengthMeter value={form.password} />
              </Grid>
            </Grid>
            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              loading={busy}
              disabled={form.password.length < MIN_PASSWORD_LENGTH || !form.firstName.trim() || !form.lastName.trim()}
              fullWidth
            >
              Create account and join
            </LoadingButton>
            <Button onClick={signInHere}>I already have an account</Button>
          </Stack>
        </Box>
      );
    }
  }

  return <AuthLayout maxWidth={480}>{body}</AuthLayout>;
}
